import { TypeManagerDecorator } from "../../../main/type.map.service";
import { ChangeDetectionStrategy, Component, computed, effect, linkedSignal, signal } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { s25Resource } from "../../../util/s25.resource";
import { ACTIVITY_TYPE_LABELS, ENGAGEMENT_LABELS } from "./engagement.labels";
import { EngagementBadge, EngagementBadgeCriteria, EngagementService } from "./engagement.service";
import { S25FilestoreImageComponent } from "../../../standalone/s25.filestore.image.component";
import { S25Const } from "../../../util/s25-const";
import { S25Util } from "../../../util/s25-util";
import { AlertService } from "../../../services/alert.service";

type CriteriaDraft = {
    localId: string;
    criteriaId?: number;
    activityTypeKey: string;
    thresholdCount: number;
    timeWindowDays: number | undefined;
};

type BadgeDraft = {
    localId: string;
    badgeId?: number;
    badgeKey: string;
    name: string;
    description: string;
    badgeGroup: string;
    tier: number;
    isVisible: boolean;
    isActive: boolean;
    iconUrl: string;
    criteria: CriteriaDraft[];
    isNew?: boolean;
};

function criteriaToDraft(c: EngagementBadgeCriteria, idx: number): CriteriaDraft {
    return {
        localId: `c-${c.criteriaId ?? idx}-${c.activityTypeKey}`,
        criteriaId: c.criteriaId,
        activityTypeKey: c.activityTypeKey,
        thresholdCount: c.thresholdCount,
        timeWindowDays: c.timeWindowDays,
    };
}

function badgeToDraft(b: EngagementBadge): BadgeDraft {
    return {
        localId: String(b.badgeId),
        badgeId: b.badgeId,
        badgeKey: b.badgeKey,
        name: b.name,
        description: b.description ?? "",
        badgeGroup: b.badgeGroup ?? "",
        tier: b.tier ?? 1,
        isVisible: b.isVisible,
        isActive: b.isActive,
        iconUrl: b.iconUrl ?? "",
        criteria: (b.criteria ?? []).map(criteriaToDraft),
    };
}

function emptyBadgeDraft(): BadgeDraft {
    return {
        localId: S25Util.randomUUID(),
        badgeKey: "",
        name: "",
        description: "",
        badgeGroup: "",
        tier: 1,
        isVisible: true,
        isActive: true,
        iconUrl: "",
        criteria: [],
        isNew: true,
    };
}

@TypeManagerDecorator("s25-ng-se-badge-config")
@Component({
    selector: "s25-ng-se-badge-config",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, FormsModule, S25FilestoreImageComponent],
    template: `
        <div class="se-badge-config">
            <h2 class="se-badge-config__title">{{ labels.badgesTitle }}</h2>

            @if (flashMessage()) {
                <div class="se-badge-config__flash" role="status">{{ flashMessage() }}</div>
            }
            @if (opError()) {
                <div class="se-badge-config__error" role="alert">{{ opError() }}</div>
            }

            @if (badgesResource.resource.isLoading() && !badgesResource.lastValue()) {
                <p class="se-badge-config__muted">Loading badges…</p>
            } @else if (badgesResource.resource.error()) {
                <p class="se-badge-config__error" role="alert">{{ badgesResource.resource.error()!.message }}</p>
            } @else {
                @if (badgeDrafts().length > 1) {
                    <div class="se-badge-config__toolbar">
                        <input
                            type="text"
                            class="se-badge-config__filter-input c-input"
                            placeholder="Filter by name or key…"
                            [ngModel]="filterText()"
                            (ngModelChange)="filterText.set($event)"
                        />
                        <div class="se-badge-config__bulk-actions">
                            <button
                                type="button"
                                class="aw-button aw-button--outline aw-button--small"
                                (click)="expandAll()"
                            >
                                Expand all
                            </button>
                            <button
                                type="button"
                                class="aw-button aw-button--outline aw-button--small"
                                (click)="collapseAll()"
                            >
                                Collapse all
                            </button>
                        </div>
                    </div>
                }
                @for (draft of filteredBadgeDrafts(); track draft.localId) {
                    <article class="se-badge-config__card">
                        <header
                            class="se-badge-config__card-head se-badge-config__card-head--toggle"
                            (click)="toggleCollapsed(draft.localId)"
                        >
                            <span class="se-badge-config__toggle-icon">{{
                                isCollapsed(draft.localId) ? "▶" : "▼"
                            }}</span>
                            <span class="se-badge-config__card-summary">
                                {{ draft.name || draft.badgeKey || "New badge" }}
                                @if (draft.badgeKey && draft.name) {
                                    <span class="se-badge-config__card-key">({{ draft.badgeKey }})</span>
                                }
                            </span>
                        </header>
                        @if (!isCollapsed(draft.localId)) {
                            <div class="se-badge-config__card-body">
                                <div class="se-badge-config__card-head">
                                    <label class="se-badge-config__field">
                                        <span>Badge key <span class="se-badge-config__req">*</span></span>
                                        <input
                                            type="text"
                                            class="se-badge-config__input c-input"
                                            [ngModel]="draft.badgeKey"
                                            (ngModelChange)="patchBadge(draft.localId, { badgeKey: $event })"
                                        />
                                        @if (isDuplicateKey(draft)) {
                                            <span class="se-badge-config__error-inline">Duplicate badge key</span>
                                        }
                                    </label>
                                </div>
                                <div class="se-badge-config__grid">
                                    <label class="se-badge-config__field">
                                        <span>Name <span class="se-badge-config__req">*</span></span>
                                        <input
                                            type="text"
                                            class="se-badge-config__input se-badge-config__input--wide c-input"
                                            [ngModel]="draft.name"
                                            (ngModelChange)="patchBadge(draft.localId, { name: $event })"
                                        />
                                    </label>
                                    <label class="se-badge-config__field">
                                        <span>Description</span>
                                        <textarea
                                            class="se-badge-config__textarea c-input"
                                            rows="2"
                                            [ngModel]="draft.description"
                                            (ngModelChange)="patchBadge(draft.localId, { description: $event })"
                                        ></textarea>
                                    </label>
                                    <label class="se-badge-config__field">
                                        <span>Badge group</span>
                                        <input
                                            type="text"
                                            class="se-badge-config__input c-input"
                                            [ngModel]="draft.badgeGroup"
                                            (ngModelChange)="patchBadge(draft.localId, { badgeGroup: $event })"
                                        />
                                    </label>
                                    <label class="se-badge-config__field">
                                        <span>Tier</span>
                                        <input
                                            type="number"
                                            class="se-badge-config__input c-input"
                                            min="1"
                                            [ngModel]="draft.tier"
                                            (ngModelChange)="
                                                patchBadge(draft.localId, { tier: coercePositiveInt($event, 1) })
                                            "
                                        />
                                    </label>
                                    <label class="se-badge-config__field se-badge-config__check">
                                        <input
                                            type="checkbox"
                                            class="c-input"
                                            [ngModel]="draft.isVisible"
                                            (ngModelChange)="patchBadge(draft.localId, { isVisible: $event })"
                                        />
                                        <span>Visible (off = secret)</span>
                                    </label>
                                    <label class="se-badge-config__field se-badge-config__check">
                                        <input
                                            type="checkbox"
                                            class="c-input"
                                            [ngModel]="draft.isActive"
                                            (ngModelChange)="patchBadge(draft.localId, { isActive: $event })"
                                        />
                                        <span>Active</span>
                                    </label>
                                </div>

                                <div class="se-badge-config__icon-section">
                                    <label class="se-badge-config__field-label">Badge icon</label>
                                    <s25-ng-filestore-image
                                        [value]="draft.iconUrl"
                                        (valueChange)="onIconSave(draft.localId, $event)"
                                        [objectType]="-1"
                                        [objectId]="-1"
                                        [fileKey]="badgeFileKey(draft.badgeId)"
                                        [saveButton]="false"
                                        [saveTrigger]="imageSaveTrigger(draft.localId)"
                                    />
                                </div>

                                <h4 class="se-badge-config__criteria-title">Award Criteria</h4>
                                <table class="se-badge-config__criteria">
                                    <thead>
                                        <tr>
                                            <th>Activity type</th>
                                            <th>Threshold</th>
                                            <th>Time window (days)</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        @for (row of draft.criteria; track row.localId) {
                                            <tr>
                                                <td>
                                                    <select
                                                        class="se-badge-config__select c-input"
                                                        [ngModel]="row.activityTypeKey"
                                                        (ngModelChange)="
                                                            patchCriterion(draft.localId, row.localId, {
                                                                activityTypeKey: $event,
                                                            })
                                                        "
                                                    >
                                                        @for (opt of activityTypeOptions(); track opt) {
                                                            <option [value]="opt">{{ activityTypeLabel(opt) }}</option>
                                                        }
                                                    </select>
                                                </td>
                                                <td>
                                                    <input
                                                        type="number"
                                                        class="se-badge-config__input c-input"
                                                        min="1"
                                                        [ngModel]="row.thresholdCount"
                                                        (ngModelChange)="
                                                            patchCriterion(draft.localId, row.localId, {
                                                                thresholdCount: coercePositiveInt($event, 1),
                                                            })
                                                        "
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="number"
                                                        class="se-badge-config__input c-input"
                                                        min="0"
                                                        placeholder="Optional"
                                                        [ngModel]="row.timeWindowDays"
                                                        (ngModelChange)="
                                                            patchCriterion(draft.localId, row.localId, {
                                                                timeWindowDays: parseOptionalWindow($event),
                                                            })
                                                        "
                                                    />
                                                </td>
                                                <td>
                                                    <button
                                                        type="button"
                                                        class="se-badge-config__linkish"
                                                        (click)="removeCriterion(draft.localId, row.localId)"
                                                    >
                                                        Remove
                                                    </button>
                                                </td>
                                            </tr>
                                        }
                                    </tbody>
                                </table>
                                <button
                                    type="button"
                                    class="aw-button aw-button--outline aw-button--small"
                                    (click)="addCriterion(draft.localId)"
                                >
                                    Add criterion
                                </button>
                                <div class="se-badge-config__card-actions">
                                    <button
                                        type="button"
                                        class="aw-button aw-button--primary aw-button--small"
                                        [disabled]="savingKey() === draft.localId"
                                        (click)="saveBadge(draft.localId)"
                                    >
                                        {{ draft.isNew ? "Create" : "Save" }}
                                    </button>
                                    @if (!draft.isNew) {
                                        <button
                                            type="button"
                                            class="aw-button aw-button--outline aw-button--small"
                                            (click)="deleteBadge(draft.localId)"
                                        >
                                            Delete
                                        </button>
                                    } @else {
                                        <button
                                            type="button"
                                            class="aw-button aw-button--outline aw-button--small"
                                            (click)="discardNewBadge(draft.localId)"
                                        >
                                            Discard
                                        </button>
                                    }
                                </div>
                            </div>
                        }
                    </article>
                } @empty {
                    <p class="se-badge-config__muted">
                        {{ filterText() ? "No badges match the filter." : "No badges configured." }}
                    </p>
                }

                <button type="button" class="aw-button aw-button--outline" (click)="addNewBadge()">Add badge</button>
            }
        </div>
    `,
    styles: [
        `
            :host {
                display: block;
            }
            .se-badge-config__title {
                margin: 0 0 1rem;
            }
            .se-badge-config__card {
                border: 1px solid var(--color--border);
                border-radius: 8px;
                padding: 1rem 1.25rem;
                margin-bottom: 1.5rem;
                background: var(--color--surface-2-bg);
            }
            .se-badge-config__toolbar {
                display: flex;
                flex-wrap: wrap;
                align-items: center;
                gap: 0.75rem;
                margin-bottom: 1rem;
            }
            .se-badge-config__filter-input {
                flex: 1 1 14rem;
                max-width: 24rem;
                box-sizing: border-box;
            }
            .se-badge-config__bulk-actions {
                display: flex;
                gap: 0.5rem;
            }
            .se-badge-config__card-head--toggle {
                display: flex;
                align-items: center;
                gap: 0.5rem;
                cursor: pointer;
                user-select: none;
                padding: 0.25rem 0;
                margin-bottom: 0;
            }
            .se-badge-config__toggle-icon {
                font-size: 0.75rem;
                color: var(--color--text-muted);
                flex-shrink: 0;
            }
            .se-badge-config__card-summary {
                font-weight: 600;
                font-size: 0.95rem;
            }
            .se-badge-config__card-key {
                font-weight: 400;
                color: var(--color--text-muted);
                font-size: 0.85rem;
            }
            .se-badge-config__card-body {
                margin-top: 0.75rem;
            }
            .se-badge-config__card-head {
                display: flex;
                flex-wrap: wrap;
                align-items: center;
                justify-content: flex-start;
                gap: 0.75rem;
                margin-bottom: 1rem;
            }
            .se-badge-config__key {
                font-size: 0.9rem;
            }
            .se-badge-config__card-actions {
                display: flex;
                gap: 0.75rem;
                margin-top: 0.75rem;
            }
            .se-badge-config__grid {
                display: grid;
                grid-template-columns: repeat(auto-fill, minmax(12rem, 1fr));
                gap: 0.75rem 1rem;
                margin-bottom: 1rem;
            }
            .se-badge-config__field--span {
                grid-column: 1 / -1;
            }
            .se-badge-config__field {
                display: flex;
                flex-direction: column;
                gap: 0.25rem;
                font-size: 0.875rem;
            }
            .se-badge-config__field.se-badge-config__check {
                flex-direction: row;
                align-items: center;
                gap: 0.5rem;
            }
            .se-badge-config__req {
                color: var(--color--danger);
            }
            .se-badge-config__input {
                width: 100%;
                max-width: 16rem;
                box-sizing: border-box;
            }
            .se-badge-config__input--wide {
                max-width: none;
            }
            .se-badge-config__textarea {
                width: 100%;
                box-sizing: border-box;
                resize: vertical;
                padding: 0.35rem 0.5rem;
            }
            .se-badge-config__select {
                min-width: 12rem;
                max-width: 100%;
            }
            .se-badge-config__icon-section {
                margin: 0.75rem 0;
                padding: 0.75rem 0;
                border-top: 1px solid var(--color--border);
            }
            .se-badge-config__field-label {
                display: block;
                font-weight: 600;
                font-size: 0.875rem;
                margin-bottom: 0.35rem;
            }
            .se-badge-config__criteria-title {
                margin: 0.5rem 0 0.35rem;
                font-size: 0.95rem;
            }
            .se-badge-config__criteria {
                width: 100%;
                border-collapse: collapse;
                margin-bottom: 0.5rem;
                font-size: 0.875rem;
            }
            .se-badge-config__criteria th,
            .se-badge-config__criteria td {
                text-align: left;
                padding: 0.35rem 0.5rem;
                border-bottom: 1px solid var(--color--border);
            }
            .se-badge-config__linkish {
                background: none;
                border: none;
                color: var(--color--link-text);
                cursor: pointer;
                text-decoration: underline;
                padding: 0;
                font: inherit;
            }
            .se-badge-config__flash {
                padding: 0.5rem 0.75rem;
                margin-bottom: 1rem;
                background: #e8f5e9;
                border-radius: 4px;
            }
            .se-badge-config__error {
                padding: 0.5rem 0.75rem;
                margin-bottom: 1rem;
                background: #ffebee;
                border-radius: 4px;
                color: var(--color--danger);
            }
            .se-badge-config__error-inline {
                color: var(--color--danger);
                font-size: 0.85rem;
            }
            .se-badge-config__muted {
                color: var(--color--text-muted);
            }
        `,
    ],
})
export class SeBadgeConfigComponent {
    readonly labels = ENGAGEMENT_LABELS;

    badgeFileKey(badgeId: number | undefined): string | undefined {
        if (!badgeId) return undefined;
        return `badge&${badgeId}`;
    }

    readonly imageSaveTriggers = signal<Record<string, number>>({});

    imageSaveTrigger(localId: string): number {
        return this.imageSaveTriggers()[localId] ?? 0;
    }

    private triggerImageSave(localId: string): void {
        this.imageSaveTriggers.update((t) => ({ ...t, [localId]: (t[localId] ?? 0) + 1 }));
    }

    readonly badgesResource = s25Resource(() => EngagementService.getBadges());
    readonly activityTypesResource = s25Resource(() => EngagementService.getActivityTypes());

    readonly badgeDrafts = linkedSignal<BadgeDraft[]>(() => {
        const list = this.badgesResource.value();
        if (!list?.length) {
            return [];
        }
        return list.map(badgeToDraft);
    });

    readonly activityTypeOptions = computed((): string[] => {
        const fromApi = this.activityTypesResource.value()?.map((t) => t.activityTypeKey) ?? [];
        const fromLabels = Object.keys(ACTIVITY_TYPE_LABELS);
        return [...new Set([...fromApi, ...fromLabels])].sort();
    });

    readonly filterText = signal("");
    readonly filteredBadgeDrafts = computed(() => {
        const term = this.filterText().trim().toLowerCase();
        const drafts = this.badgeDrafts();
        if (!term) return drafts;
        return drafts.filter((d) => d.name.toLowerCase().includes(term) || d.badgeKey.toLowerCase().includes(term));
    });

    readonly collapsedSet = signal<Set<string>>(new Set());
    readonly savingKey = signal<string | undefined>(undefined);
    readonly flashMessage = signal<string | undefined>(undefined);
    readonly opError = signal<string | undefined>(undefined);

    readonly activityTypeLabel = (key: string) => ACTIVITY_TYPE_LABELS[key] ?? key;

    isCollapsed(localId: string): boolean {
        return this.collapsedSet().has(localId);
    }

    toggleCollapsed(localId: string): void {
        this.collapsedSet.update((set) => {
            const next = new Set(set);
            if (next.has(localId)) next.delete(localId);
            else next.add(localId);
            return next;
        });
    }

    expandAll(): void {
        this.collapsedSet.set(new Set());
    }

    collapseAll(): void {
        this.collapsedSet.set(new Set(this.badgeDrafts().map((d) => d.localId)));
    }

    coercePositiveInt(value: string | number | null, fallback: number): number {
        const n = Number(value);
        if (!Number.isFinite(n) || n < 1) {
            return fallback;
        }
        return Math.floor(n);
    }

    parseOptionalWindow(value: string | number | null): number | undefined {
        if (value === "" || value === null || value === undefined) {
            return undefined;
        }
        const n = Number(value);
        if (!Number.isFinite(n) || n <= 0) {
            return undefined;
        }
        return Math.floor(n);
    }

    patchBadge(draftLocalId: string, patch: Partial<Omit<BadgeDraft, "localId" | "criteria">>): void {
        this.badgeDrafts.update((drafts) => drafts.map((d) => (d.localId === draftLocalId ? { ...d, ...patch } : d)));
    }

    patchCriterion(draftLocalId: string, criterionLocalId: string, patch: Partial<CriteriaDraft>): void {
        this.badgeDrafts.update((drafts) =>
            drafts.map((d) => {
                if (d.localId !== draftLocalId) return d;
                return {
                    ...d,
                    criteria: d.criteria.map((c) => (c.localId === criterionLocalId ? { ...c, ...patch } : c)),
                };
            }),
        );
    }

    addCriterion(draftLocalId: string): void {
        const firstKey = this.activityTypeOptions()[0] ?? "LOGIN";
        this.badgeDrafts.update((drafts) =>
            drafts.map((d) =>
                d.localId === draftLocalId
                    ? {
                          ...d,
                          criteria: [
                              ...d.criteria,
                              {
                                  localId: S25Util.randomUUID(),
                                  activityTypeKey: firstKey,
                                  thresholdCount: 1,
                                  timeWindowDays: undefined,
                              },
                          ],
                      }
                    : d,
            ),
        );
    }

    removeCriterion(draftLocalId: string, criterionLocalId: string): void {
        this.badgeDrafts.update((drafts) =>
            drafts.map((d) =>
                d.localId === draftLocalId
                    ? { ...d, criteria: d.criteria.filter((c) => c.localId !== criterionLocalId) }
                    : d,
            ),
        );
    }

    addNewBadge(): void {
        this.badgeDrafts.update((drafts) => [...drafts, emptyBadgeDraft()]);
    }

    discardNewBadge(draftLocalId: string): void {
        this.badgeDrafts.update((drafts) => drafts.filter((d) => d.localId !== draftLocalId));
    }

    isDuplicateKey(draft: BadgeDraft): boolean {
        const key = draft.badgeKey.trim().toLowerCase();
        if (!key) return false;
        return this.badgeDrafts().some((d) => d.localId !== draft.localId && d.badgeKey.trim().toLowerCase() === key);
    }

    private draftToPayload(d: BadgeDraft) {
        return {
            badgeKey: d.badgeKey.trim(),
            name: d.name,
            description: d.description || undefined,
            badgeGroup: d.badgeGroup || undefined,
            tier: d.tier,
            isVisible: d.isVisible,
            isActive: d.isActive,
            iconUrl: d.iconUrl || undefined,
            criteria: d.criteria.map((c) => ({
                criteriaId: c.criteriaId,
                activityTypeKey: c.activityTypeKey,
                thresholdCount: c.thresholdCount,
                timeWindowDays: c.timeWindowDays,
            })),
        };
    }

    async saveBadge(draftLocalId: string): Promise<void> {
        const d = this.badgeDrafts().find((x) => x.localId === draftLocalId);
        if (!d) return;

        const key = d.badgeKey.trim();
        if (!key || !d.name.trim()) {
            this.opError.set("Badge key and name are required.");
            return;
        }

        if (this.isDuplicateKey(d)) {
            this.opError.set("Badge key must be unique.");
            return;
        }

        this.savingKey.set(draftLocalId);
        this.opError.set(undefined);
        try {
            const payload = this.draftToPayload(d);
            if (d.isNew) {
                const created = await EngagementService.createBadge(payload);
                if (created?.badgeId) {
                    this.badgeDrafts.update((drafts) =>
                        drafts.map((x) =>
                            x.localId === draftLocalId ? { ...x, badgeId: created.badgeId, isNew: false } : x,
                        ),
                    );
                    this.triggerImageSave(draftLocalId);
                }
                this.flashMessage.set("Badge created.");
            } else {
                await EngagementService.updateBadge(d.badgeId!, payload);
                this.triggerImageSave(draftLocalId);
                this.flashMessage.set("Badge saved.");
                await this.badgesResource.resource.reload();
            }
        } catch (e) {
            this.opError.set(S25Util.errorText(e));
        } finally {
            this.savingKey.set(undefined);
        }
    }

    onIconSave(draftLocalId: string, iconUrl: string): void {
        this.patchBadge(draftLocalId, { iconUrl });
        const d = this.badgeDrafts().find((x) => x.localId === draftLocalId);
        if (d?.badgeId && iconUrl) {
            EngagementService.updateBadge(d.badgeId, this.draftToPayload({ ...d, iconUrl }));
        }
    }

    async deleteBadge(draftLocalId: string): Promise<void> {
        const d = this.badgeDrafts().find((x) => x.localId === draftLocalId);
        if (!d || !d.badgeId) return;
        const label = d.name || d.badgeKey;
        if (!(await AlertService.confirm(`Delete badge "${label}"? This cannot be undone.`))) return Promise.resolve();
        await EngagementService.deleteBadge(d.badgeId!);
        this.flashMessage.set("Badge deleted.");
        await this.badgesResource.resource.reload();
    }

    constructor() {
        effect((onCleanup) => {
            const msg = this.flashMessage();
            if (!msg) {
                return;
            }
            const t = window.setTimeout(() => this.flashMessage.set(undefined), 4000);
            onCleanup(() => window.clearTimeout(t));
        });
    }
}
